import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2 } from "lucide-react";
import { useAccessResolver } from "@/hooks/useAccessResolver";
import { routeFromAccess } from "@/lib/routeFromAccess";
import { AccessErrorScreen } from "@/components/AccessErrorScreen";
import { InviteInvalidScreen } from "@/components/InviteInvalidScreen";
import { storeInviteToken, getInviteToken, clearInviteToken, isLiveInvite } from "@/lib/inviteTokenStorage";
import { setViewContextFromInvite } from "@/contexts/ViewContext";
import { useQueryClient } from "@tanstack/react-query";
import { AuthSplitPage, type AuthMode } from "@/components/auth/AuthSplitPage";
import { InviteAuthScreens, type InviteAuthMode } from "@/components/invite/InviteAuthScreens";
import {
  mobileEmailInputProps,
  mobilePasswordInputProps,
  readFormField,
} from "@/lib/mobileAuthInputs";

// Unified invite: member_invites (primary) or legacy cleaner_invites / actor_invites
type InviteData =
  | { type: "member"; email: string; inviteRole: string; propertyName?: string | null; inviterName?: string | null }
  | { type: "legacy"; companyId: string; email: string; fullName: string | null; propertyName?: string | null; inviterName?: string | null };

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");
  const rawInviteToken = searchParams.get("invite_token");
  const inviteToken = (rawInviteToken != null && rawInviteToken.trim() !== "") ? rawInviteToken.trim() : null;
  const modeSignup = searchParams.get("mode") === "signup";
  const inviteCompanyId = searchParams.get("invite_company"); // Legacy support
  const queryClient = useQueryClient();
  const { signIn, signUp, signOut, user, profile, loading, refetchProfile } = useAuth();
  const { data: access, isLoading: accessLoading } = useAccessResolver();
  const { toast } = useToast();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(() => !!(inviteToken || getInviteToken()));
  const [inviteAuthMode, setInviteAuthMode] = useState<InviteAuthMode>(modeSignup ? "signup" : "login");
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showProfileError, setShowProfileError] = useState(false);
  const profileRetryDoneRef = useRef(false);

  // When user is set but profile is missing: retry once after 1.5s, then show error UI after 4s
  useEffect(() => {
    if (!user || profile != null) {
      setShowProfileError(false);
      profileRetryDoneRef.current = false;
      return;
    }
    const retryTimer = setTimeout(() => {
      if (profileRetryDoneRef.current) return;
      profileRetryDoneRef.current = true;
      refetchProfile();
    }, 1500);
    const errorTimer = setTimeout(() => setShowProfileError(true), 4000);
    return () => {
      clearTimeout(retryTimer);
      clearTimeout(errorTimer);
    };
  }, [user, profile, refetchProfile]);

  // Resolve invite from URL or leftover localStorage. Dead tokens are dropped so they cannot block sign-in.
  useEffect(() => {
    const tokenToResolve = (inviteToken || getInviteToken() || "").trim();
    if (!tokenToResolve) {
      setLoadingInvite(false);
      return;
    }
    setLoadingInvite(true);
    const dropDeadInvite = () => {
      clearInviteToken();
      setInviteData(null);
      if (inviteToken) {
        setSearchParams(
          (prev) => {
            if (!prev.has("invite_token")) return prev;
            const next = new URLSearchParams(prev);
            next.delete("invite_token");
            return next;
          },
          { replace: true },
        );
      }
    };
    const resolveInvite = async () => {
      const { data: memberInvite } = await supabase.rpc("get_member_invite_by_token", {
        p_token: tokenToResolve,
      });
      if (memberInvite) {
        if (isLiveInvite(memberInvite)) {
          storeInviteToken(tokenToResolve);
          setInviteData({
            type: "member",
            email: memberInvite.email,
            inviteRole: memberInvite.invite_role,
            propertyName: (memberInvite as { property_name?: string | null }).property_name ?? null,
            inviterName: (memberInvite as { inviter_name?: string | null }).inviter_name ?? null,
          });
          setEmail(memberInvite.email ?? "");
        } else {
          dropDeadInvite();
        }
        setLoadingInvite(false);
        return;
      }

      const { data: cohostInvite } = await supabase.rpc("get_cohost_invite_by_token", {
        p_token: tokenToResolve,
      });
      const cohost = cohostInvite as {
        invited_email?: string;
        property_name?: string | null;
        inviter_name?: string | null;
        accepted_at?: string | null;
        revoked_at?: string | null;
        expires_at?: string | null;
      } | null;
      if (cohost) {
        if (isLiveInvite(cohost)) {
          storeInviteToken(tokenToResolve);
          setInviteData({
            type: "member",
            email: cohost.invited_email ?? "",
            inviteRole: "cohost",
            propertyName: cohost.property_name ?? null,
            inviterName: cohost.inviter_name ?? null,
          });
          setEmail(cohost.invited_email ?? "");
        } else {
          dropDeadInvite();
        }
        setLoadingInvite(false);
        return;
      }

      const { data: legacyInvite, error } = await supabase
        .from("cleaner_invites")
        .select("company_id, email, full_name, accepted")
        .eq("token", tokenToResolve)
        .maybeSingle();
      if (!error && legacyInvite && !legacyInvite.accepted) {
        storeInviteToken(tokenToResolve);
        setInviteData({
          type: "legacy",
          companyId: legacyInvite.company_id,
          email: legacyInvite.email,
          fullName: legacyInvite.full_name,
        });
        setEmail(legacyInvite.email);
        if (legacyInvite.full_name) {
          const nameParts = legacyInvite.full_name.split(" ");
          setFirstName(nameParts[0] || "");
          setLastName(nameParts.slice(1).join(" ") || "");
        }
      } else {
        dropDeadInvite();
      }
      setLoadingInvite(false);
    };
    void resolveInvite();
  }, [inviteToken, setSearchParams]);

  // Accept member invite when logged in with token (unified invite flow)
  useEffect(() => {
    if (!user || !profile || !getInviteToken()) return;
    const run = async () => {
      const token = getInviteToken();
      if (!token) return;
      const { data: memberInvite } = await supabase.rpc("get_member_invite_by_token", { p_token: (token ?? "").trim() });
      if (!memberInvite) return; // Legacy token; access resolver handles it
      if (!isLiveInvite(memberInvite)) {
        clearInviteToken();
        return;
      }
      const userEmail = user.email?.toLowerCase().trim();
      if (!userEmail || userEmail !== (memberInvite.email ?? "").toLowerCase()) return;
      try {
        const { error } = await supabase.rpc("accept_member_invite", { p_token: (token ?? "").trim() });
        if (error) {
          const alreadyAccepted = /already accepted|not found.*accepted/i.test(error.message);
          if (alreadyAccepted) {
            clearInviteToken();
            await refetchProfile();
            await queryClient.invalidateQueries({ queryKey: ["cleaner"] });
            const role = (memberInvite as { invite_role?: string })?.invite_role;
            const propertyId = (memberInvite as { property_id?: string })?.property_id;
            const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
            if (role === "cohost") {
              setViewContextFromInvite("owner");
              navigate(`/owner/properties${q}`, { replace: true });
            } else if (role === "cleaner") {
              setViewContextFromInvite("cleaner");
              navigate(`/worker/schedule${q}`, { replace: true });
            } else {
              setViewContextFromInvite("owner");
              navigate("/owner/properties", { replace: true });
            }
            return;
          }
          toast({ title: "Invite acceptance failed", description: error.message, variant: "destructive" });
          return;
        }
        clearInviteToken();
        await refetchProfile();
        await queryClient.invalidateQueries({ queryKey: ["access-resolver"] });
        await queryClient.invalidateQueries({ queryKey: ["user-capabilities"] });
        await queryClient.invalidateQueries({ queryKey: ["cleaner"] });
        const role = (memberInvite as { invite_role?: string })?.invite_role;
        const propertyId = (memberInvite as { property_id?: string })?.property_id;
        if (role === "cohost") {
          setViewContextFromInvite("owner");
          const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
          navigate(`/owner/properties${q}`, { replace: true });
        } else if (role === "cleaner") {
          setViewContextFromInvite("cleaner");
          const q = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
          navigate(`/worker/schedule${q}`, { replace: true });
        } else {
          setViewContextFromInvite("owner");
          navigate("/owner/properties", { replace: true });
        }
      } catch (err) {
        toast({ title: "Invite acceptance failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
      }
    };
    run();
  }, [user?.id, user?.email, profile?.id, profile?.role, refetchProfile, toast, navigate]);

  // Redirect if already logged in. Only a live invite may take priority over the dashboard.
  useEffect(() => {
    if (!loading && user && profile) {
      if (loadingInvite) return;
      const pendingToken = getInviteToken();
      if (pendingToken && inviteData) {
        navigate(`/invite?token=${encodeURIComponent(pendingToken)}`, { replace: true });
        return;
      }
      if (pendingToken && !inviteData) {
        clearInviteToken();
      }
      setIsSubmitting(false);

      if (profile.role === "cleaner") {
        if (accessLoading) return;
        if (access?.type === "full_dashboard" && access.workspaceLock === "properties") {
          navigate("/owner/properties", { replace: true });
          return;
        }
        // Team on one unit and Cleaner on another: open the workspace they used last.
        if (
          access?.type === "full_dashboard" &&
          !access.workspaceLock &&
          typeof window !== "undefined" &&
          window.localStorage.getItem("view_context") === "owner"
        ) {
          navigate("/owner/properties", { replace: true });
          return;
        }
        if (typeof window !== "undefined") window.location.replace(`${window.location.origin}/worker/schedule`);
        return;
      }

      // Owners / co-hosts: wait for access resolver then route
      if (!accessLoading) {
        if (redirectUrl) {
          navigate(redirectUrl, { replace: true });
          return;
        }
        if (access?.type === "no_access") {
          navigate("/", { replace: true });
          return;
        }
        const route = routeFromAccess(access, accessLoading);
        if (route) {
          navigate(route, { replace: true });
        }
      }
    } else if (!loading && user && !profile) {
      setIsSubmitting(false);
      if (showProfileError) console.warn("User logged in but profile is missing after retries.");
    }
  }, [user, profile, loading, loadingInvite, inviteData, access, accessLoading, navigate, redirectUrl, showProfileError]);

  /** Build /login path preserving invite_token and redirect; set mode for toggle links. Must be before any early return (hooks order). */
  const getLoginPath = useCallback(
    (params: { mode?: AuthMode }) => {
      const next = new URLSearchParams(searchParams);
      if (params.mode) next.set("mode", params.mode);
      else next.delete("mode");
      const q = next.toString();
      return `/login${q ? `?${q}` : ""}`;
    },
    [searchParams]
  );

  /** Update mode in URL in-place for smooth transition (no full navigation). */
  const handleToggleMode = useCallback(
    (mode: AuthMode) => {
      const next = new URLSearchParams(searchParams);
      next.set("mode", mode);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // iOS autofill often fills the DOM without updating React state — read the form live.
    const liveEmail = readFormField(e.currentTarget, "email", email).trim();
    const livePassword = readFormField(e.currentTarget, "password", password);
    setEmail(liveEmail);
    setPassword(livePassword);

    if (!liveEmail || !livePassword) {
      toast({
        title: "Email and password required",
        description: "Enter your full email (with @) and password.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    if (!liveEmail.includes("@")) {
      toast({
        title: "Enter a full email",
        description: "Include the @ — e.g. you@company.com",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      console.log("Attempting sign in...");
      const { error } = await signIn(liveEmail, livePassword);
      
      if (error) {
        console.error("[Login] signIn error", { status: (error as { status?: number })?.status, message: error.message });
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      console.log("Sign in successful, waiting for profile and redirect...");
      // Sign in successful - the useEffect will handle redirect when profile loads
      // Set a timeout as fallback in case profile never loads
      setTimeout(() => {
        // This will only fire if redirect hasn't happened (isSubmitting still true)
        // The useEffect should have set isSubmitting to false on redirect
        console.log("Timeout check - isSubmitting:", isSubmitting, "user:", !!user, "profile:", !!profile);
      }, 3000);
      
      // Don't set isSubmitting to false here - let the redirect useEffect handle it
    } catch (err) {
      console.error("Sign in exception:", err);
      toast({
        title: "Sign in failed",
        description: err instanceof Error ? err.message : "An unexpected error occurred",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();

    const liveEmail = (emailRef.current?.value || email).trim();
    setEmail(liveEmail);
    
    if (!liveEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address to reset your password.",
        variant: "destructive",
      });
      return;
    }
    
    setIsResettingPassword(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(liveEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        toast({
          title: "Password reset failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Check your email",
          description: "We've sent you a password reset link. Please check your inbox.",
        });
      }
    } catch (err) {
      toast({
        title: "Password reset failed",
        description: err instanceof Error ? err.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const liveEmail = readFormField(e.currentTarget, "email", email).trim();
    const livePassword = readFormField(e.currentTarget, "password", password);
    setEmail(liveEmail);
    setPassword(livePassword);
    
    if (!firstName.trim()) {
      toast({
        title: "First name required",
        description: "Please enter your first name.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!lastName.trim()) {
      toast({
        title: "Last name required",
        description: "Add your last name so owners can tell cleaners apart (e.g. Shane M.).",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!liveEmail || !liveEmail.includes("@")) {
      toast({
        title: "Enter a full email",
        description: "Include the @ — e.g. you@company.com",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || undefined;

    // Cleaner invite → signup as cleaner (not owner). Owner/admin requires a separate account.
    const roleForSignUp =
      inviteData?.type === "member" && inviteData.inviteRole === "cleaner"
        ? "cleaner"
        : inviteData?.type === "legacy"
          ? "cleaner"
          : "owner";
    const companyIdToUse =
      inviteData?.type === "legacy" ? inviteData.companyId : inviteCompanyId ?? undefined;

    const inviteToken = searchParams.get("invite_token") || getInviteToken();
    const emailRedirectTo = inviteToken
      ? `${window.location.origin}/auth/callback?invite_token=${encodeURIComponent(inviteToken)}`
      : undefined;

    const { error } = await signUp({
      email: liveEmail,
      password: livePassword,
      role: roleForSignUp,
      fullName,
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      companyId: companyIdToUse,
      emailRedirectTo,
    });

    if (error) {
      const status = (error as { status?: number })?.status;
      const message = error?.message ?? "";
      console.error("[Login] signUp error", { status, message });
      const msg = message.toLowerCase();
      const isConfirmationEmailError =
        status === 500 ||
        msg.includes("confirmation email") ||
        msg.includes("sending confirmation email") ||
        msg.includes("error sending");
      const description = isConfirmationEmailError
        ? "Email delivery is currently being configured. Please try again in a few minutes or use Sign In if you already confirmed."
        : message;
      toast({
        title: "Sign up failed",
        description,
        variant: "destructive",
      });
    } else {
      if (inviteToken && inviteData?.type === "legacy") {
        await supabase.from("cleaner_invites").update({ accepted: true }).eq("token", inviteToken);
      }
      if (inviteToken) {
        setShowCheckEmail(true);
        setInviteAuthMode("check-email");
      } else {
        toast({
          title: "Check your email",
          description: "Please verify your email address to continue.",
        });
      }
    }
    
    setIsSubmitting(false);
  };

  // User signed in but profile failed to load — show error with retry / logout (stops infinite "Loading...")
  if (user && !profile && showProfileError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background p-4">
        <p className="text-muted-foreground text-center">Couldn&apos;t load your account.</p>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => {
              setShowProfileError(false);
              profileRetryDoneRef.current = false;
              refetchProfile();
            }}
          >
            Try again
          </Button>
          <Button type="button" variant="outline" onClick={() => signOut()}>
            Log out
          </Button>
        </div>
      </div>
    );
  }

  // Show loading until auth + profile are ready; team/cleaner see "Redirecting..." (we redirect without waiting for access)
  const isRedirectingTeamOrCleaner =
    user && profile && (profile.role === "company_admin" || profile.role === "cleaner" || profile.role === "pending_invite");
  if (loading || loadingInvite || (accessLoading && profile && !isRedirectingTeamOrCleaner) || (user && !profile)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>{isRedirectingTeamOrCleaner ? "Redirecting..." : "Loading..."}</p>
      </div>
    );
  }

  // Handle invalid invite - show specific error screen (NO dashboard fallback)
  if (user && profile && access?.type === "invite_invalid") {
    return <InviteInvalidScreen reason={access.reason} />;
  }

  // If user is logged in but access cannot be resolved, show error screen
  if (user && profile && access?.type === "no_access") {
    return <AccessErrorScreen />;
  }

  // Handle unauthorized
  if (user && profile && access?.type === "unauthorized") {
    return <AccessErrorScreen />;
  }

  // If user is logged in, access resolver will handle redirect via useEffect
  // Don't show login form if user is logged in
  if (user && profile && access) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Redirecting...</p>
      </div>
    );
  }

  const hasInvite = !!(inviteToken || inviteCompanyId || inviteData);
  const isSignUp = modeSignup;

  const authForm = isSignUp ? (
    <form onSubmit={handleSignUp} className="space-y-4 min-w-0 overflow-hidden" aria-label="Create account">
      {hasInvite && (
        <Alert className="mb-4">
          <Building2 className="h-4 w-4" />
          <AlertDescription>
            {inviteData?.type === "member"
              ? inviteData.inviteRole === "cleaner"
                ? "You've been invited as a cleaner. Sign in or create an account to accept."
                : "You've been invited to help manage a property. Sign in or create an account to accept."
              : "You've been invited to join a cleaning company. Create your account to get started."}
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="signup-first-name">First Name</Label>
          <Input
            id="signup-first-name"
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-last-name">Last Name</Label>
          <Input
            id="signup-last-name"
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            autoComplete="family-name"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          required
          readOnly={!!inviteData?.email}
          className={inviteData?.email ? "bg-muted cursor-not-allowed" : ""}
          {...mobileEmailInputProps}
          ref={emailRef}
        />
        {inviteData?.email && (
          <p className="text-xs text-muted-foreground">Email is locked to the invite address.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <Input
          id="signup-password"
          ref={passwordRef}
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          required
          minLength={6}
          name="password"
          autoComplete="new-password"
        />
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="link"
          className="px-0 text-sm"
          onClick={handleForgotPassword}
          disabled={isResettingPassword}
        >
          {isResettingPassword ? "Sending..." : "Forgot password?"}
        </Button>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Creating account..." : "Create account"}
      </Button>
    </form>
  ) : (
    <form onSubmit={handleSignIn} className="space-y-4 min-w-0 overflow-hidden" aria-label="Sign in">
      {hasInvite && (
        <Alert className="mb-4">
          <Building2 className="h-4 w-4" />
          <AlertDescription>
            {inviteData?.type === "member"
              ? inviteData.inviteRole === "cleaner"
                ? "You've been invited as a cleaner. Sign in or create an account to accept."
                : "You've been invited to help manage a property. Sign in or create an account to accept."
              : "You've been invited to join a cleaning company. Create your account to get started."}
          </AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          ref={emailRef}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          required
          {...mobileEmailInputProps}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="signin-password">Password</Label>
        <Input
          id="signin-password"
          ref={passwordRef}
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          required
          {...mobilePasswordInputProps}
        />
      </div>
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="link"
          className="px-0 text-sm"
          onClick={handleForgotPassword}
          disabled={isResettingPassword}
        >
          {isResettingPassword ? "Sending..." : "Forgot password?"}
        </Button>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );

  if (inviteToken || showCheckEmail) {
    return (
      <InviteAuthScreens
        mode={showCheckEmail ? "check-email" : inviteAuthMode}
        onModeChange={(m) => {
          setShowCheckEmail(false);
          setInviteAuthMode(m);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (m === "signup") next.set("mode", "signup");
            else next.delete("mode");
            return next;
          });
        }}
        email={email}
        onEmailChange={setEmail}
        emailLocked={!!inviteData?.email}
        password={password}
        onPasswordChange={setPassword}
        firstName={firstName}
        onFirstNameChange={setFirstName}
        lastName={lastName}
        onLastNameChange={setLastName}
        propertyName={inviteData?.propertyName}
        inviterName={inviteData?.inviterName}
        isSubmitting={isSubmitting}
        isResettingPassword={isResettingPassword}
        onSubmitSignIn={handleSignIn}
        onSubmitSignUp={handleSignUp}
        onForgotPassword={handleForgotPassword}
        onResend={handleForgotPassword}
      />
    );
  }

  return (
    <AuthSplitPage
      mode={isSignUp ? "signup" : "login"}
      getLoginPath={getLoginPath}
      onToggleMode={handleToggleMode}
    >
      {authForm}
    </AuthSplitPage>
  );
};

export default Login;
